import {z} from 'zod';
import {NativeLiveTurnSchema, type NativeMessage} from './native-hermes-types.js';

/** This projection controls which native conversation records reach customers. */
export const internalConversationKinds = new Set(['auto_continue','process_complete','async_delegation_complete','model_switch']);
const syntheticFlags = ['_todo_snapshot_synthetic','_empty_recovery_synthetic','_verification_stop_synthetic',
  '_pre_verify_synthetic','_dropped_toolcall_nudge'];
const taskHeader = '[Your active task list was preserved across context compression]';
const skillHeader = '[Skills pruned during compression — reload before acting on these tasks]';
function record(value: unknown): Record<string,unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string,unknown> : null;
}
function internal(row: Record<string,unknown>): boolean {
  return internalConversationKinds.has(String(row.display_kind ?? ''))
    || record(row.display_metadata)?._eight_internal_notification === true;
}

/** Remove recognized machine-injected notices, retaining a preceding human input. */
export function customerUserText(text: string): string | null {
  let visible = text;
  for (const header of [taskHeader,skillHeader]) {
    const position = visible.indexOf(header);
    if (position === 0 || position > 0 && /\n\s*$/.test(visible.slice(0,position))) visible=visible.slice(0,position).trimEnd();
  }
  if (/^\s*\[(?:System:|IMPORTANT: Background process )/.test(visible)) return null;
  return visible.trim() ? visible : null;
}

export function customerMessages(value: unknown, rawValue: unknown): NativeMessage[] {
  const rows = z.array(z.record(z.string(),z.unknown())).parse(value);
  const raw = z.array(z.record(z.string(),z.unknown())).parse(rawValue);
  const originals = new Map(raw.filter(row => typeof row._row_id === 'number').map(row => [row._row_id,row]));
  return rows.flatMap(row => {
    if (row.role !== 'user' && row.role !== 'assistant' || internal(row)) return [];
    const original = originals.get(row.row_id);
    if (original && internal(original)) return [];
    if (typeof row.text !== 'string') return [];
    const text = row.role === 'user' ? customerUserText(row.text) : row.text;
    if (!text?.trim() || original && row.role === 'user' && syntheticFlags.some(flag => original[flag] === true)
      && text === row.text) return [];
    // Never forward model content, tool arguments/results, reasoning, or arbitrary sidecars.
    const result: NativeMessage = {role: row.role,text};
    if (typeof row.row_id === 'number') result.row_id=row.row_id;
    if (typeof row.timestamp === 'number' || typeof row.timestamp === 'string') result.timestamp=row.timestamp;
    if (typeof row.display_kind === 'string') result.display_kind=row.display_kind;
    const metadata = record(row.display_metadata);
    if (typeof metadata?.delivered_to_model === 'boolean') result.display_metadata={delivered_to_model:metadata.delivered_to_model};
    return [result];
  });
}

export function customerLiveTurn(value: unknown): z.infer<typeof NativeLiveTurnSchema> | null {
  if (value === null) return null;
  const turn = NativeLiveTurnSchema.parse(value);
  const metadata = record(turn.display_metadata);
  const user = internal(turn) ? '' : customerUserText(turn.user) ?? '';
  const corrections = turn.corrections ?? [], offsets = turn.correction_offsets ?? [];
  if (corrections.length !== offsets.length) throw new Error('The live turn has mismatched correction positions.');
  const retained = corrections.flatMap((text,index) => {
    const visible=customerUserText(text);
    return visible === null ? [] : [{text:visible,offset:offsets[index]!}];
  });
  const {display_metadata: _metadata,...visibleTurn}=turn;
  return {...visibleTurn,user,corrections:retained.map(item => item.text),correction_offsets:retained.map(item => item.offset),
    ...(typeof metadata?.delivered_to_model === 'boolean' ? {display_metadata:{delivered_to_model:metadata.delivered_to_model}} : {})};
}

/** Workspace replay needs invalidation and cursor position, not internal event payloads. */
export function customerEvents(value: unknown): Array<Record<string,unknown>> {
  return z.array(z.record(z.string(),z.unknown())).parse(value).map(row => {
    const projected: Record<string,unknown>={type:z.string().parse(row.type)};
    for (const key of ['seq','timestamp']) if (typeof row[key] === 'number') projected[key]=row[key];
    return projected;
  });
}
